import React, { useEffect, useState } from 'react';
import { Button, Modal, Spin } from 'antd';
import { CloseOutlined, ExclamationCircleOutlined, RightOutlined } from '@ant-design/icons';
import { orderPreRatingApi } from '../../../../services/process/order-pre-rating';
import type {
  OrderPreProfitMode,
  OrderPreRatingDetail,
  OrderPreRiskHitItem,
} from '../../../../types/process/order-pre-rating';
import {
  PROFIT_LEVEL_MAP,
  PROFIT_MODE_MAP,
  RISK_BASE_TYPE_MAP,
  RISK_LEVEL_MAP,
} from '../../../../types/process/order-pre-rating';
import riskIcon from './assets/pre-rating-risk-icon.svg';
import hitIcon from './assets/pre-rating-hit-icon.svg';
import dangerIcon from './assets/pre-rating-danger-icon.svg';
import profitIcon from './assets/pre-rating-profit-icon.svg';
import ProfitEstimationModal from './ProfitEstimationModal';
import './PreRatingModal.css';

interface PreRatingModalProps {
  orderNo: string;
  visible: boolean;
  onClose: () => void;
}

const RISK_CARD_TONE: Record<string, { border: string; background: string; color: string }> = {
  HIGH: { border: '#ef4444', background: 'rgba(239,68,68,0.1)', color: '#ef4444' },
  LOW: { border: '#00a63e', background: 'rgba(0,166,62,0.1)', color: '#00a63e' },
  UNKNOWN: { border: '#98a2b3', background: 'rgba(152,162,179,0.12)', color: '#667085' },
};

const PROFIT_CARD_TONE: Record<string, { border: string; background: string; color: string }> = {
  HIGH: { border: '#00a63e', background: 'rgba(0,166,62,0.1)', color: '#00a63e' },
  LOW: { border: '#f59e0b', background: 'rgba(245,158,11,0.12)', color: '#d97706' },
  UNKNOWN: { border: '#98a2b3', background: 'rgba(152,162,179,0.12)', color: '#667085' },
};

const formatPercent = (value?: number | null) => {
  if (value === undefined || value === null || Number.isNaN(value)) {
    return '--';
  }

  const percent = value * 100;
  if (Math.abs(percent - Math.round(percent)) < 0.0001) {
    return `${Math.round(percent)}%`;
  }
  if (Math.abs(percent * 10 - Math.round(percent * 10)) < 0.0001) {
    return `${percent.toFixed(1)}%`;
  }
  return `${percent.toFixed(2)}%`;
};

const formatRiskScore = (value?: number | null) => {
  if (value === undefined || value === null || Number.isNaN(value)) {
    return '--';
  }
  return `${value}分`;
};

const resolveRiskItemText = (item: OrderPreRiskHitItem) =>
  item.name || item.message || item.code || '未命名风控项';

const resolveModeLabel = (mode?: string) => {
  if (!mode) return '待识别';
  return PROFIT_MODE_MAP[mode] || mode;
};

const resolveRiskBaseLabel = (baseType?: string) => {
  if (!baseType) return '待评级';
  return RISK_BASE_TYPE_MAP[baseType] || baseType;
};

const PreRatingModal: React.FC<PreRatingModalProps> = ({ orderNo, visible, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [preRating, setPreRating] = useState<OrderPreRatingDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [profitEstimationVisible, setProfitEstimationVisible] = useState(false);

  useEffect(() => {
    if (!visible || !orderNo) return;

    setLoading(true);
    setError(null);
    setPreRating(null);

    orderPreRatingApi
      .getPreRating(orderNo)
      .then((res) => {
        if (res.code === 200 && res.data) {
          setPreRating(res.data);
          return;
        }
        setError(res.message || '暂无评级数据');
      })
      .catch(() => setError('加载预评级失败，请稍后重试'))
      .finally(() => setLoading(false));
  }, [visible, orderNo]);

  useEffect(() => {
    if (!visible) {
      setProfitEstimationVisible(false);
    }
  }, [visible]);

  const riskLevel = preRating?.risk.level || 'UNKNOWN';
  const profitLevel = preRating?.profit.level || 'UNKNOWN';
  const riskTone = RISK_CARD_TONE[riskLevel] || RISK_CARD_TONE.UNKNOWN;
  const profitTone = PROFIT_CARD_TONE[profitLevel] || PROFIT_CARD_TONE.UNKNOWN;
  const isRiskUnknown = riskLevel === 'UNKNOWN';
  const isProfitUnknown = profitLevel === 'UNKNOWN';
  const riskHitItems = preRating?.risk.hitItems || [];
  const deductionTotal = preRating?.risk.deductionTotal ?? riskHitItems.reduce((sum, item) => sum + (item.deductScore || 0), 0);
  const otherModes = (preRating?.profit.otherModes || []).filter((mode) => mode.mode !== preRating?.profit.mainstreamMode);
  const riskThreshold = preRating?.risk.threshold ?? 80;
  const mainstreamModeThresholdRate = isProfitUnknown || !preRating?.profit.mainstreamMode
    ? '--'
    : formatPercent(preRating.profit.thresholdRate);

  const renderBody = () => {
    if (loading) {
      return (
        <div className="pre-rating-modal__status">
          <Spin size="large" />
        </div>
      );
    }

    if (error) {
      return (
        <div className="pre-rating-modal__status">
          <div className="pre-rating-modal__status-error">
            <ExclamationCircleOutlined style={{ fontSize: 28, color: '#f59e0b' }} />
            <p>{error}</p>
          </div>
        </div>
      );
    }

    if (!preRating) {
      return (
        <div className="pre-rating-modal__status">
          <div className="pre-rating-modal__status-error">
            <ExclamationCircleOutlined style={{ fontSize: 28, color: '#98a2b3' }} />
            <p>暂无评级数据</p>
          </div>
        </div>
      );
    }

    return (
      <div className="pre-rating-modal__content">
        <div className="pre-rating-modal__column">
          <section className="pre-rating-modal__section">
            <div className="pre-rating-modal__section-title-row">
              <div className="pre-rating-modal__section-title">
                <img className="pre-rating-modal__section-icon" src={riskIcon} alt="" />
                <p className="pre-rating-modal__section-label">风控预评级</p>
              </div>
            </div>

            <div
              className="pre-rating-modal__score-card"
              style={{ borderColor: riskTone.border, background: riskTone.background }}
            >
              <p className="pre-rating-modal__score-card-value" style={{ color: riskTone.color }}>
                {isRiskUnknown ? '待评级' : formatRiskScore(preRating.risk.score)}
              </p>
              <p className="pre-rating-modal__score-card-note">
                {isRiskUnknown ? '风控报告尚未准备好，待风控完成后生成评分' : '最终风控分 = 基础分 - 扣分合计值'}
              </p>
              <p className="pre-rating-modal__score-card-rule">
                评级规则：{riskThreshold}分及以上为低风险，{riskThreshold}分以下为高风险
              </p>
            </div>

            <div className="pre-rating-modal__metric-card">
              <div>
                <p className="pre-rating-modal__metric-card-title">
                  风控类型：{resolveRiskBaseLabel(preRating.risk.baseType)}
                </p>
                <p className="pre-rating-modal__metric-card-desc">基于人人租平台的风控结果</p>
              </div>
              <p className="pre-rating-modal__metric-card-value">基础分：{isRiskUnknown ? '--' : (preRating.risk.baseScore ?? '--')}</p>
            </div>
          </section>

          <section className="pre-rating-modal__section">
            <div className="pre-rating-modal__section-title-row pre-rating-modal__section-title-row--between">
              <div className="pre-rating-modal__section-title">
                <img className="pre-rating-modal__section-icon" src={hitIcon} alt="" />
                <p className="pre-rating-modal__section-label">命中风控项 ({riskHitItems.length}项)</p>
              </div>
              <p className="pre-rating-modal__section-extra">扣分合计值：{deductionTotal}</p>
            </div>

            {riskHitItems.length > 0 ? (
              <div className="pre-rating-modal__risk-list">
                {riskHitItems.map((item) => (
                  <div className="pre-rating-modal__risk-item" key={`${item.code}-${item.sort}-${item.name}`}>
                    <div className="pre-rating-modal__risk-item-main">
                      <img className="pre-rating-modal__risk-item-icon" src={dangerIcon} alt="" />
                      <p className="pre-rating-modal__risk-item-text">{resolveRiskItemText(item)}</p>
                    </div>
                    <p className="pre-rating-modal__risk-item-score">-{item.deductScore}分</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="pre-rating-modal__empty">
                {RISK_LEVEL_MAP[riskLevel]?.label === '待评级'
                  ? '风控报告尚未准备好，当前订单仍在等待评级。'
                  : '当前订单未命中风控项。'}
              </div>
            )}
          </section>
        </div>

        <div className="pre-rating-modal__column">
          <section className="pre-rating-modal__section">
            <div className="pre-rating-modal__section-title-row">
              <div className="pre-rating-modal__section-title">
                <img className="pre-rating-modal__section-icon" src={profitIcon} alt="" />
                <p className="pre-rating-modal__section-label">利润预评级</p>
              </div>
            </div>

            <div
              className="pre-rating-modal__profit-card"
              style={{ borderColor: profitTone.border, background: profitTone.background }}
            >
              <button
                type="button"
                className="pre-rating-modal__profit-card-arrow"
                onClick={() => setProfitEstimationVisible(true)}
                aria-label="查看利润测算"
                title="查看利润测算"
              >
                <RightOutlined />
              </button>
              <p className="pre-rating-modal__profit-card-value" style={{ color: profitTone.color }}>
                {isProfitUnknown ? '待评级' : formatPercent(preRating.profit.rate)}
              </p>
              <p className="pre-rating-modal__profit-card-note">
                {PROFIT_LEVEL_MAP[profitLevel]?.label === '待评级' ? '待计算利润率' : '预估利润率'}
              </p>
            </div>

            <div className="pre-rating-modal__metric-card">
              <div>
                <div className="pre-rating-modal__metric-card-title-row">
                  <p className="pre-rating-modal__metric-card-title">{resolveModeLabel(preRating.profit.mainstreamMode)}</p>
                  <span className="pre-rating-modal__metric-card-inline-meta">最低利润率 {mainstreamModeThresholdRate}</span>
                </div>
                <p className="pre-rating-modal__metric-card-desc">基于历史数据的最可能业务模式</p>
              </div>
              <p className="pre-rating-modal__metric-card-value">
                历史占比 {isProfitUnknown || !preRating.profit.mainstreamMode ? '--' : formatPercent(preRating.profit.mainstreamModeRatio)}
              </p>
            </div>
          </section>

          <section className="pre-rating-modal__other-modes">
            <p className="pre-rating-modal__other-modes-title">其他业务模式</p>
            <div className="pre-rating-modal__other-modes-spacer" />
            <div className="pre-rating-modal__table-card">
              {otherModes.length > 0 ? (
                <div className="pre-rating-modal__table-wrap">
                  <table className="pre-rating-modal__table">
                    <thead>
                      <tr>
                        <th>业务模式</th>
                        <th>最低利润率</th>
                        <th>历史订单占比</th>
                      </tr>
                    </thead>
                    <tbody>
                      {otherModes.map((mode: OrderPreProfitMode) => (
                        <tr key={`${mode.mode}-${mode.ratio}`}>
                          <td>{resolveModeLabel(mode.mode)}</td>
                          <td className="pre-rating-modal__table-profit">{formatPercent(mode.thresholdProfitRate)}</td>
                          <td>{formatPercent(mode.ratio)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="pre-rating-modal__empty">暂无其他业务模式数据。</div>
              )}
            </div>
          </section>
        </div>
      </div>
    );
  };

  return (
    <>
      <Modal
        open={visible}
        onCancel={onClose}
        footer={null}
        width={920}
        centered
        destroyOnClose
        className="pre-rating-modal"
        closeIcon={<CloseOutlined />}
        styles={{ body: { padding: 0 } }}
        style={{ maxWidth: 'calc(100vw - 32px)' }}
      >
        <div className="pre-rating-modal__shell">
          <div className="pre-rating-modal__header">
            <div className="pre-rating-modal__title">
              订单预评级详情 - {preRating?.orderNo || orderNo}
            </div>
          </div>
          <div className="pre-rating-modal__body">{renderBody()}</div>
          <div className="pre-rating-modal__footer">
            <Button type="primary" block className="pre-rating-modal__close-button" onClick={onClose}>
              关闭
            </Button>
          </div>
        </div>
      </Modal>
      <ProfitEstimationModal
        orderNo={orderNo}
        visible={profitEstimationVisible}
        onClose={() => setProfitEstimationVisible(false)}
      />
    </>
  );
};

export default PreRatingModal;
